import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Divider, Tab, Tabs } from '@mui/material';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { FlowControlPerformMode, OutputPreviewTab } from '@/types/player/simulation-cue-sheet.types';
import { useTranslations } from '@/i18n/use-translations';
import { PreviewAudioToggleButton } from '@/components/preview/preview-audio-toggle-button';
import { SharedLiveKitRoomProvider } from '@/components/preview/shared-livekit-room-provider';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { getOnAirLivekitRoomIdFromFlow } from '@/helpers/entity-ref.helper';
import { FlowControlOutputPreview } from './flow-control-output-preview';
import { FlowControlNextCuePreview, LIVE_NEXT_PAINTED_NONE } from './flow-control-next-cue-preview';
import { useGoLivePreviewHold } from '@/state/stores/use-go-live-preview-hold.store';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { currentPreviewShowsCueItem } from '@/helpers/resolve-output-preview-layers';
import { FlowControlPreviewFrameLabel } from './flow-control-preview-frame-label';
import { PerformShowModeOverlay } from '@/components/flows/detail/edit-details/perform-view/perform-show-mode-overlay';
import { PerformFocusModeOverlay } from '@/components/flows/detail/edit-details/perform-view/perform-focus-mode-overlay';

/**
 * Absolute max wait after Play is hidden. Does not restart when Now/Next readiness
 * flickers (LiveKit SID updates, still-frame remounts).
 */
const PREVIEW_READY_GRACE_MS = 3000;

export interface FlowControlNowNextStackProps {
  flow: FlowDto;
  performMode: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  planFlowId?: string;
  entityRef: string;
  liveContextQueryUpdatedAt?: number;
}

/** Stacked Now (live/sim output) + Next (upcoming cue preview with advance). Equal frame cells. */
export function FlowControlNowNextStack({
  flow,
  performMode,
  liveContextState,
  planFlowId,
  entityRef,
  liveContextQueryUpdatedAt,
}: FlowControlNowNextStackProps) {
  const t = useTranslations();
  const isSimulation = performMode === 'simulation';
  const mountPreviewConsumer = useGoLivePreviewHold((state) => state.mountPreviewConsumer);
  const unmountPreviewConsumer = useGoLivePreviewHold((state) => state.unmountPreviewConsumer);
  useEffect(() => {
    mountPreviewConsumer();
    return () => unmountPreviewConsumer();
  }, [mountPreviewConsumer, unmountPreviewConsumer]);
  const holdOwner = useGoLivePreviewHold((state) => state.owner);
  const previewHoldOwner = resolveLiveBindingFlowId(flow);
  const previewsHeld = holdOwner === previewHoldOwner;
  const releasePreviewHold = useGoLivePreviewHold((state) => state.release);
  const liveContextForHold = useLiveContextStateWithLivePlayState(entityRef, liveContextState);
  const cueForHold = useLiveContextCueState(entityRef, undefined, liveContextForHold?.cueState);
  const [paintedNextItemId, setPaintedNextItemId] = useState<string | null>(null);
  const [tab, setTab] = useState<OutputPreviewTab>('stageRoom');
  const [currentPreviewReady, setCurrentPreviewReady] = useState(false);
  const [currentReadyTab, setCurrentReadyTab] = useState(tab);
  if (tab !== currentReadyTab) {
    setCurrentReadyTab(tab);
    setCurrentPreviewReady(false);
  }
  const currentShowsCueItem =
    performMode === 'play' && currentPreviewShowsCueItem(liveContextForHold, cueForHold?.currentStep?.itemId);

  useEffect(() => {
    if (!previewsHeld) {
      return;
    }
    const nextItemId = cueForHold?.nextItemId;
    const nextPainted = nextItemId
      ? paintedNextItemId === nextItemId
      : Boolean(cueForHold?.currentStep?.itemId) && paintedNextItemId === LIVE_NEXT_PAINTED_NONE;
    if (!currentPreviewReady || !currentShowsCueItem || !nextPainted) {
      return;
    }
    releasePreviewHold(previewHoldOwner);
  }, [
    currentPreviewReady,
    currentShowsCueItem,
    cueForHold?.currentStep?.itemId,
    cueForHold?.nextItemId,
    paintedNextItemId,
    previewHoldOwner,
    previewsHeld,
    releasePreviewHold,
  ]);
  const nowLabel = isSimulation
    ? t('flows.detail.control.nowNext.simulation', { defaultValue: 'SIMULATION' })
    : t('flows.detail.control.nowNext.now', { defaultValue: 'On air' });
  const nowColor = isSimulation ? 'info.main' : 'error.main';
  const [previewAudioEnabled, setPreviewAudioEnabled] = useState(false);
  const [viewsReady, setViewsReady] = useState(false);

  const nowReadyRef = useRef(false);
  const nextReadyRef = useRef(false);
  const viewsReadyRef = useRef(false);
  const nextCueIdentityRef = useRef('');
  const loadingStartedAtRef = useRef<number | null>(null);
  const graceTimerRef = useRef<number | null>(null);

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current != null) {
      window.clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const unlockPlay = useCallback(() => {
    clearGraceTimer();
    loadingStartedAtRef.current = null;
    viewsReadyRef.current = true;
    setViewsReady(true);
  }, [clearGraceTimer]);

  const ensureGraceTimer = useCallback(() => {
    if (viewsReadyRef.current || graceTimerRef.current != null) {
      return;
    }
    if (loadingStartedAtRef.current == null) {
      loadingStartedAtRef.current = Date.now();
    }
    const remaining = Math.max(0, PREVIEW_READY_GRACE_MS - (Date.now() - loadingStartedAtRef.current));
    graceTimerRef.current = window.setTimeout(() => {
      graceTimerRef.current = null;
      viewsReadyRef.current = true;
      setViewsReady(true);
    }, remaining);
  }, []);

  const recomputeViewsReady = useCallback(() => {
    if (nowReadyRef.current && nextReadyRef.current) {
      unlockPlay();
      return;
    }
    if (viewsReadyRef.current) {
      // Latched until Next cue identity changes.
      return;
    }
    ensureGraceTimer();
  }, [ensureGraceTimer, unlockPlay]);

  useEffect(() => () => clearGraceTimer(), [clearGraceTimer]);

  const handleNowReadyChange = useCallback(
    (ready: boolean) => {
      setCurrentPreviewReady(ready);
      if (nowReadyRef.current === ready) {
        return;
      }
      nowReadyRef.current = ready;
      recomputeViewsReady();
    },
    [recomputeViewsReady],
  );

  const handleNextReadyChange = useCallback(
    (ready: boolean) => {
      if (nextReadyRef.current === ready) {
        return;
      }
      nextReadyRef.current = ready;
      recomputeViewsReady();
    },
    [recomputeViewsReady],
  );

  const handleNextCueIdentityChange = useCallback(
    (identity: string) => {
      if (identity === nextCueIdentityRef.current) {
        return;
      }
      nextCueIdentityRef.current = identity;
      clearGraceTimer();
      loadingStartedAtRef.current = Date.now();
      viewsReadyRef.current = false;
      setViewsReady(false);
      if (nowReadyRef.current && nextReadyRef.current) {
        unlockPlay();
        return;
      }
      ensureGraceTimer();
    },
    [clearGraceTimer, ensureGraceTimer, unlockPlay],
  );

  const planFlowIdForRoom = planFlowId ?? (flow.origin ? getObjectId(flow.origin) : flow.id);
  const livekitRoomId = isSimulation
    ? undefined
    : getOnAirLivekitRoomIdFromFlow(planFlowIdForRoom ?? '', flow.liveContext);

  return (
    <SharedLiveKitRoomProvider livekitRoomId={livekitRoomId}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          gap: 1,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, value: OutputPreviewTab) => setTab(value)}
            sx={{ 'minHeight': 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
          >
            <Tab
              value="stageRoom"
              label={t('flows.detail.control.outputPreview.tabs.stageRoom', { defaultValue: 'Stage / Room' })}
            />
            <Tab
              value="live"
              label={t('flows.detail.control.outputPreview.tabs.live', { defaultValue: 'Broadcast' })}
            />
          </Tabs>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <PreviewAudioToggleButton
            variant="inline"
            audioEnabled={previewAudioEnabled}
            onToggle={() => setPreviewAudioEnabled((prev) => !prev)}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateRows: '1fr 1fr',
            gap: 1,
            overflow: 'hidden',
            width: '100%',
            position: 'relative',
          }}
        >
          <Box sx={{ minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <FlowControlOutputPreview
              flow={flow}
              performMode={performMode}
              liveContextState={liveContextState}
              planFlowId={planFlowId}
              showTabs={false}
              tab={tab}
              onTabChange={setTab}
              previewAudioEnabled={previewAudioEnabled}
              onPreviewAudioEnabledChange={setPreviewAudioEnabled}
              frameOverlay={
                <>
                  <FlowControlPreviewFrameLabel label={nowLabel} color={nowColor} />
                  <PerformShowModeOverlay
                    flow={flow}
                    entityRef={entityRef}
                    target="onAir"
                    performMode={performMode}
                    liveContextState={liveContextState}
                  />
                  {performMode === 'play' && flow.liveContext?.isRunning ? (
                    <PerformFocusModeOverlay entityRef={entityRef} liveContextState={liveContextState} />
                  ) : null}
                </>
              }
              frameBorderColor={nowColor}
              frameBorderWidth={2}
              onReadyChange={handleNowReadyChange}
            />
          </Box>
          <Box sx={{ minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <FlowControlNextCuePreview
              flow={flow}
              performMode={performMode}
              liveContextState={liveContextState}
              planFlowId={planFlowId}
              entityRef={entityRef}
              liveContextQueryUpdatedAt={liveContextQueryUpdatedAt}
              tab={tab}
              viewsReady={viewsReady}
              onReadyChange={handleNextReadyChange}
              onCueIdentityChange={handleNextCueIdentityChange}
              onLiveNextPainted={setPaintedNextItemId}
            />
          </Box>
          {previewsHeld ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'background.paper',
                zIndex: 2,
              }}
            />
          ) : null}
        </Box>
      </Box>
    </SharedLiveKitRoomProvider>
  );
}
